import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BEGRIFFE } from '../../api/src/pakete/kern/begriffe.js';
import { api } from '../lib/api.js';
import { useAuth } from '../state/auth.jsx';
import { useTimer } from '../state/timer.jsx';
import { load } from '../lib/storage.js';
import { clock } from '../lib/format.js';
import { Icon } from '../components/Icon.jsx';

/**
 * Anmeldung mit dem Exxas-Benutzer. Liegt als Overlay über der App, damit nach einer abgelaufenen
 * Sitzung nichts verloren geht. Passwort-Manager funktionieren (autocomplete-Attribute, echtes Formular).
 */
export function LoginScreen() {
  const { login, reason } = useAuth();
  const timer = useTimer();
  const [benutzer, setBenutzer] = useState(() => load('benutzer', ''));
  // Firmen-Code: aus dem Link (?firma=…) oder vom letzten Mal; das Feld erscheint nur, wenn die Adresse nicht reicht
  const [firma, setFirma] = useState(() => new URLSearchParams(window.location.search).get('firma') || load('firma', '') || '');
  const info = useQuery({ queryKey: ['mandant-info'], queryFn: () => api('/mandant'), staleTime: Infinity, retry: 1 });
  const [codeFeld, setCodeFeld] = useState(false);
  const mitCode = codeFeld || !!info.data?.codeNoetig || !!firma;
  const firmenName = info.data?.mandant?.name === 'WorkStudio' ? null : info.data?.mandant?.name;
  // Vor der Anmeldung kein /api/me: zuletzt bekannter Produktname der Firma (Sitzung abgelaufen) oder Kern-Standard
  const produkt = useQueryClient().getQueryData(['me'])?.mandant?.begriffe?.produkt || BEGRIFFE.produkt;
  const [passwort, setPasswort] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pwRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => { (benutzer ? pwRef : userRef).current?.focus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [ausstehend, setAusstehend] = useState(false);
  const submit = async (e, { verwerfen = false } = {}) => {
    e?.preventDefault();
    setError('');
    if (!benutzer.trim() || !passwort) { setError('Bitte Benutzername und Passwort eingeben.'); return; }
    setBusy(true);
    try {
      await login(benutzer.trim(), passwort, mitCode ? firma.trim() : '', { verwerfen });
    } catch (err) {
      setAusstehend(err.code === 'AUSSTEHEND');
      if (err.code === 'FIRMA_NOETIG' || err.code === 'FIRMA_UNBEKANNT') setCodeFeld(true);
      else if (err.code !== 'AUSSTEHEND') setPasswort('');
      setError(err.message);
      pwRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login" role="dialog" aria-modal="true" aria-labelledby="login-title">
      <form className="login-card" onSubmit={submit} noValidate>
        <span className="login-logo"><Icon name="marke" size={30} stroke={2.6} /></span>
        <div className="stack" style={{ gap: 4 }}>
          <h1 id="login-title" className="h1">{produkt}</h1>
          <span className="sub">{firmenName ? `${firmenName} · ` : ''}Mit deinem Exxas-Benutzer anmelden</span>
        </div>

        {reason === 'SESSION_EXPIRED' && (
          <div className="notice info" role="status"><Icon name="clock" size={18} />Deine Sitzung ist abgelaufen. Bitte neu anmelden – du machst danach dort weiter, wo du warst.</div>
        )}
        {timer?.running && (
          <div className="notice info" role="status"><Icon name="clock" size={18} />Zeit läuft weiter ({clock(timer.elapsedSec)} auf {timer.nummer}). Nach der Anmeldung kannst du sie wie gewohnt stoppen und buchen.</div>
        )}

        {mitCode && (
          <label className="field">
            <span className="label">Firmen-Code</span>
            <input className="input" name="organization" autoComplete="organization" autoCapitalize="none" autoCorrect="off" spellCheck={false}
              value={firma} onChange={(e) => setFirma(e.target.value)} maxLength={60} placeholder="von eurem Büro" aria-describedby="firma-hilfe" />
            <span id="firma-hilfe" className="sub" style={{ fontSize: 12 }}>Steht in der Einladung eurer Firma.</span>
          </label>
        )}

        <label className="field">
          <span className="label">Benutzername</span>
          <input ref={userRef} className="input" name="username" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            inputMode="email" value={benutzer} onChange={(e) => setBenutzer(e.target.value)} maxLength={120} placeholder="vorname.nachname@firma.ch" aria-invalid={!!error} />
        </label>
        <div className="field">
          <label className="label" htmlFor="login-pw">Passwort</label>
          <span style={{ position: 'relative', display: 'block' }}>
            <input id="login-pw" ref={pwRef} className="input" name="password" type={show ? 'text' : 'password'} autoComplete="current-password"
              value={passwort} onChange={(e) => setPasswort(e.target.value)} maxLength={200} style={{ paddingRight: 104 }} aria-invalid={!!error} />
            <button type="button" className="linkbtn" onClick={() => setShow((x) => !x)} aria-pressed={show} aria-controls="login-pw"
              style={{ position: 'absolute', right: 6, top: 4, height: 48, padding: '0 10px' }}>{show ? 'Verbergen' : 'Anzeigen'}</button>
          </span>
        </div>

        {error && <div className="notice err" role="alert"><Icon name="warn" size={18} />{error}</div>}
        {ausstehend && (
          <button type="button" className="btn secondary block" disabled={busy}
            onClick={() => { if (window.confirm('Die noch nicht gespeicherten Einträge werden auf diesem Gerät gelöscht. Wirklich verwerfen?')) submit(null, { verwerfen: true }); }}>
            Verwerfen und anmelden
          </button>
        )}

        <button type="submit" className="btn lg block" disabled={busy}>{busy ? 'Anmelden …' : 'Anmelden'}</button>
        <span className="sub" style={{ fontSize: 12, textAlign: 'center' }}>Gleiche Zugangsdaten wie in Exxas. Das Passwort wird nicht gespeichert.</span>
      </form>
    </div>
  );
}
